import type { Profile } from 'models/ota/ProfileModel'
import type { ProfileModuleVersion } from 'models/ota/ProfileModuleVersionModel'
import type { ExistingVehicle } from 'models/Vehicles/VehicleModel'

// Newest release first. Profiles without a release time go last.
const compareByReleaseDateDescending = (a: Profile, b: Profile) =>
    (b.releaseDate?.getTime() ?? -Infinity) - (a.releaseDate?.getTime() ?? -Infinity)

// The profiles a vehicle can be updated to: all of them except the installed one, newest release first
export const getAvailableProfiles = (profiles: ReadonlyArray<Profile>, installedProfileId: string | null): Profile[] =>
    profiles
        .filter(profile => profile.id !== installedProfileId)
        .sort(compareByReleaseDateDescending)

// The vehicles' installed profile ids, null for a vehicle whose installed profile is not known
export const getInstalledProfileIds = (vehicles: ReadonlyArray<Pick<ExistingVehicle, 'vehicle_profile'>>): Set<string | null> =>
    new Set(vehicles.map(vehicle => vehicle.vehicle_profile?.installed_profile_id ?? null))

// The id of the newest released profile, or null when no profile has a release time
export const getLatestProfileId = (profiles: ReadonlyArray<Profile>): string | null => {
    const [latest] = [...profiles].sort(compareByReleaseDateDescending)
    return latest?.releaseDate ? latest.id : null
}

export interface ModuleVersionChange {
    module: string
    // null when the module is not in that profile
    fromVersion: string | null
    toVersion: string | null
}

// Matches the modules of the installed profile and the target profile by name.
// The target profile's modules come first, then the ones only in the installed profile.
export const getModuleVersionChanges = (
    installedModuleVersions: ReadonlyArray<ProfileModuleVersion> | null,
    targetModuleVersions: ReadonlyArray<ProfileModuleVersion>,
): ModuleVersionChange[] => {
    const installedVersionByModule = new Map((installedModuleVersions ?? []).map(({ module, version }) => [module, version]))
    const targetModules = new Set(targetModuleVersions.map(({ module }) => module))
    return [
        ...targetModuleVersions.map(({ module, version }) => ({
            module,
            fromVersion: installedVersionByModule.get(module) ?? null,
            toVersion: version,
        })),
        ...(installedModuleVersions ?? [])
            .filter(({ module }) => !targetModules.has(module))
            .map(({ module, version }) => ({ module, fromVersion: version, toVersion: null })),
    ]
}
